/**
 * Embedded Video utilities.
 *
 * Embed YouTube/Vimeo/Loom videos in listing descriptions.
 * Video URL stored in listing.publicData.videoUrl.
 *
 * Supports:
 *   - YouTube: https://youtube.com/watch?v=ID, https://youtu.be/ID
 *   - Vimeo: https://vimeo.com/ID
 *   - Loom: https://loom.com/share/ID
 */

export const SUPPORTED_VIDEO_PLATFORMS = ['youtube', 'vimeo', 'loom'];

/**
 * Detect video platform from URL.
 * @param {string} url
 * @returns {'youtube'|'vimeo'|'loom'|null}
 */
export const detectPlatform = (url) => {
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) return 'youtube';
    if (hostname.includes('vimeo.com')) return 'vimeo';
    if (hostname.includes('loom.com')) return 'loom';
    return null;
  } catch (_) {
    return null;
  }
};

/**
 * Extract video ID from URL.
 * @param {string} url
 * @returns {string|null}
 */
export const extractVideoId = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    // YouTube
    if (parsed.hostname.includes('youtube.com')) return parsed.searchParams.get('v');
    if (parsed.hostname.includes('youtu.be')) return parsed.pathname.slice(1);
    // Vimeo
    if (parsed.hostname.includes('vimeo.com')) return parsed.pathname.slice(1);
    // Loom
    if (parsed.hostname.includes('loom.com')) return parsed.pathname.split('/').pop();
    return null;
  } catch (_) {
    return null;
  }
};

/**
 * Build embed URL for a video.
 * @param {string} url
 * @returns {string|null}
 */
export const buildEmbedUrl = (url) => {
  const platform = detectPlatform(url);
  const id = extractVideoId(url);
  if (!platform || !id) return null;

  switch (platform) {
    case 'youtube': return `https://www.youtube.com/embed/${id}`;
    case 'vimeo': return `https://player.vimeo.com/video/${id}`;
    case 'loom': return `https://www.loom.com/embed/${id}`;
    default: return null;
  }
};

/**
 * Get video URL from listing.
 * @param {Object|null} listing
 * @returns {string|null}
 */
export const getListingVideoUrl = (listing) =>
  listing?.attributes?.publicData?.videoUrl || null;

/**
 * Validate video URL.
 * @param {string} url
 * @returns {string|null}
 */
export const validateVideoUrl = (url) => {
  if (!url?.trim()) return null; // empty = no video (OK)
  if (!detectPlatform(url)) return 'URL must be from YouTube, Vimeo, or Loom';
  if (!extractVideoId(url)) return 'Could not extract video ID from URL';
  return null;
};
